import { useCallback, useEffect, useRef, useState } from 'react';
import { LoaderCircle, Pause, Play } from 'lucide-react';
import { cn } from '@/lib/utils';

const VELOCIDADES = [1, 1.5, 2] as const;
const SALTO_S = 5;
const NO_CARGO = 'No pudimos cargar la grabación.';

export interface AudioPlayerProps
  extends Omit<React.ComponentProps<'audio'>, 'controls' | 'children' | 'src' | 'ref'> {
  src: string;
  audioRef?: React.Ref<HTMLAudioElement>;
  duracionConocida?: number | null;
  mostrarError?: boolean;
  etiqueta?: string;
}

export const tiempo = (segundos: number | null | undefined): string => {
  if (segundos === null || segundos === undefined || !Number.isFinite(segundos) || segundos < 0) return '–:––';
  const total = Math.floor(segundos);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

export function AudioPlayer({
  src,
  audioRef,
  duracionConocida = null,
  mostrarError = true,
  etiqueta = 'Grabación de la llamada',
  className,
  onPlay,
  onPause,
  onTimeUpdate,
  onError,
  onLoadedMetadata,
  onWaiting,
  onPlaying,
  preload = 'none',
  ...props
}: AudioPlayerProps) {
  const audio = useRef<HTMLAudioElement | null>(null);
  const pista = useRef<HTMLDivElement>(null);
  const arrastre = useRef<number | null>(null);
  const [sonando, setSonando] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [fallo, setFallo] = useState(false);
  const [segundo, setSegundo] = useState(0);
  const [duracion, setDuracion] = useState<number | null>(null);
  const [velocidad, setVelocidad] = useState<(typeof VELOCIDADES)[number]>(1);

  useEffect(() => {
    setSonando(false);
    setCargando(false);
    setFallo(false);
    setSegundo(0);
    setDuracion(null);
  }, [src]);

  const enlazar = useCallback(
    (elemento: HTMLAudioElement | null) => {
      audio.current = elemento;
      if (typeof audioRef === 'function') audioRef(elemento);
      else if (audioRef) (audioRef as React.MutableRefObject<HTMLAudioElement | null>).current = elemento;
    },
    [audioRef],
  );

  const total = duracion ?? duracionConocida ?? null;
  const avance = total ? Math.min(100, (segundo / total) * 100) : 0;

  const alternar = () => {
    const elemento = audio.current;
    if (!elemento) return;
    if (elemento.paused) {
      if (elemento.error) elemento.load();
      setFallo(false);
      setCargando(elemento.readyState < 3);
      void elemento.play().catch(() => {
        setCargando(false);
        setFallo(elemento.error !== null);
      });
    } else {
      elemento.pause();
    }
  };

  const irA = (segundos: number) => {
    const elemento = audio.current;
    if (!elemento || !total) return;
    const destino = Math.max(0, Math.min(total, segundos));
    elemento.currentTime = destino;
    setSegundo(destino);
  };

  const posicion = (evento: React.PointerEvent<HTMLDivElement>): number | null => {
    const caja = pista.current?.getBoundingClientRect();
    if (!caja || !total || caja.width === 0) return null;
    return Math.max(0, Math.min(total, ((evento.clientX - caja.left) / caja.width) * total));
  };

  const soltar = (evento: React.PointerEvent<HTMLDivElement>) => {
    const destino = arrastre.current;
    arrastre.current = null;
    if (evento.currentTarget.hasPointerCapture(evento.pointerId)) {
      evento.currentTarget.releasePointerCapture(evento.pointerId);
    }
    if (destino !== null) irA(destino);
  };

  const conTeclado = (evento: React.KeyboardEvent<HTMLDivElement>) => {
    const acciones: Record<string, () => void> = {
      ArrowLeft: () => irA(segundo - SALTO_S),
      ArrowRight: () => irA(segundo + SALTO_S),
      Home: () => irA(0),
      End: () => irA(total ?? 0),
    };
    const accion = acciones[evento.key];
    if (!accion) return;
    evento.preventDefault();
    accion();
  };

  const cambiarVelocidad = () => {
    const siguiente = VELOCIDADES[(VELOCIDADES.indexOf(velocidad) + 1) % VELOCIDADES.length]!;
    setVelocidad(siguiente);
    if (audio.current) audio.current.playbackRate = siguiente;
  };

  const Icono = cargando ? LoaderCircle : sonando ? Pause : Play;

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <div
        role="group"
        aria-label={etiqueta}
        className="flex min-w-0 items-center gap-3 rounded-lg border border-border bg-muted/40 px-2 py-1.5"
      >
        <button
          type="button"
          onClick={alternar}
          aria-label={sonando ? 'Pausar' : 'Reproducir'}
          className="grid size-8 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground transition-opacity duration-150 ease-out hover:opacity-90 active:scale-[0.97]"
        >
          <Icono className={cn('size-4', cargando && 'animate-spin', !sonando && !cargando && 'translate-x-px')} aria-hidden />
        </button>
        <span className="w-10 shrink-0 text-right text-xs tabular text-foreground">{tiempo(segundo)}</span>
        <div
          ref={pista}
          role="slider"
          tabIndex={0}
          aria-label="Posición en la grabación"
          aria-valuemin={0}
          aria-valuemax={Math.round(total ?? 0)}
          aria-valuenow={Math.round(segundo)}
          aria-valuetext={`${tiempo(segundo)} de ${tiempo(total)}`}
          aria-disabled={!total}
          onPointerDown={(evento) => {
            const destino = posicion(evento);
            if (destino === null) return;
            evento.currentTarget.setPointerCapture(evento.pointerId);
            arrastre.current = destino;
            setSegundo(destino);
          }}
          onPointerMove={(evento) => {
            if (arrastre.current === null) return;
            const destino = posicion(evento);
            if (destino === null) return;
            arrastre.current = destino;
            setSegundo(destino);
          }}
          onPointerUp={soltar}
          onLostPointerCapture={soltar}
          onKeyDown={conTeclado}
          className="group relative flex h-6 min-w-0 flex-1 cursor-pointer touch-none items-center rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring aria-disabled:cursor-default"
        >
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-border">
            <div className="h-full rounded-full bg-primary" style={{ width: `${avance}%` }} />
          </div>
          <span
            className="absolute size-3 -translate-x-1/2 rounded-full border-2 border-background bg-primary opacity-0 shadow-sm transition-opacity duration-150 ease-out group-hover:opacity-100 group-focus-visible:opacity-100"
            style={{ left: `${avance}%` }}
            aria-hidden
          />
        </div>
        <span className="w-10 shrink-0 text-xs tabular text-muted-foreground">{tiempo(total)}</span>
        <button
          type="button"
          onClick={cambiarVelocidad}
          aria-label={`Velocidad ${velocidad}×`}
          className="h-7 shrink-0 rounded-md px-2 text-xs font-medium tabular text-muted-foreground transition-colors duration-150 ease-out hover:bg-muted hover:text-foreground"
        >
          {velocidad}×
        </button>
        <audio
          ref={enlazar}
          src={src}
          preload={preload}
          className="hidden"
          onPlay={(evento) => {
            setSonando(true);
            onPlay?.(evento);
          }}
          onPause={(evento) => {
            setSonando(false);
            setCargando(false);
            onPause?.(evento);
          }}
          onWaiting={(evento) => {
            setCargando(true);
            onWaiting?.(evento);
          }}
          onPlaying={(evento) => {
            setCargando(false);
            onPlaying?.(evento);
          }}
          onLoadedMetadata={(evento) => {
            const valor = evento.currentTarget.duration;
            if (Number.isFinite(valor)) setDuracion(valor);
            evento.currentTarget.playbackRate = velocidad;
            onLoadedMetadata?.(evento);
          }}
          onDurationChange={(evento) => {
            const valor = evento.currentTarget.duration;
            if (Number.isFinite(valor)) setDuracion(valor);
          }}
          onTimeUpdate={(evento) => {
            const ahora = evento.currentTarget.currentTime;
            if (arrastre.current === null) {
              setSegundo((previo) => (Math.floor(ahora) === Math.floor(previo) ? previo : ahora));
            }
            onTimeUpdate?.(evento);
          }}
          onEnded={() => setSegundo(0)}
          onError={(evento) => {
            setFallo(true);
            setCargando(false);
            setSonando(false);
            onError?.(evento);
          }}
          {...props}
        />
      </div>
      {fallo && mostrarError && <p className="text-xs text-destructive">{NO_CARGO}</p>}
    </div>
  );
}
